import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import sanitizeParams from './utils/params';

interface Headers {
  'Content-Type': string;
  Accept: string;
  'MEMBERSHIP-SESSION-TOKEN': string;
  'X-API-KEY': string;
}

interface ApiError {
  error: string;
  fields?: { error: string };
}

type Response<Success> = AxiosPromise<Success>;

interface GuestWeddingBody {
  allowGuestPreview?: boolean;
  isPrivateRsvp?: boolean;
  rsvpPageHidden?: boolean;
  rsvpDeadline?: string;
  sendRsvpReminder?: boolean;
  rsvpMedium?: string;
  usesSubEvents?: boolean;
  questions?: Question[];
}

export interface GuestWeddingResponse extends GuestWeddingBody {
  id: string;
  isPrivateRsvp: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EventLocation {
  name?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export type EventType = 'Ceremony' | 'Reception' | 'WeddingEvent' | 'WeddingDay';

export interface SubEventBody {
  id?: string;
  name?: string | null;
  type?: EventType;
  date?: string | null;
  time?: string | null;
  endTime?: string | null;
  attire?: string | null;
  notes?: string | null;
  location?: EventLocation;
  visible?: boolean;
}

export interface EventBody extends SubEventBody {
  id?: string;
  order?: number;
  requireRsvp?: boolean;
  sameVenue?: boolean;
  subEvents?: SubEventBody[];
  meals?: string[];
  weddingId?: string;
  questions?: Question[];
  image?: {
    id: string;
    height: number;
    width: number;
    rotationAngle: number;
    cropBox: string;
  };
}

export interface EventResponse extends EventBody {
  id: string;
  weddingId?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface UpdateMealBody {
  meal: string;
  updatedMeal: string;
}

export interface Invitation {
  eventId: string;
  answers?: Answer[];
}

export interface Person {
  id: string;
  firstName?: string;
  lastName?: string;
  isLeader?: boolean;
  email?: string;
  phone?: string;
  invitations?: Invitation[];
  answers?: Answer[];
}

export interface HouseholdsResponse {
  people?: Person[];
}

export interface Household {
  id: string;
  weddingId?: string;
  description?: string;
  rsvpNote?: string;
  coupleNote?: string;
  groupId?: string;
  people?: Person[];
  address?: Address;
  answers?: Answer[];
}

export interface HouseholdResponse extends Household {
  weddingId: string;
}

interface Address {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  fullAddress?: string;
}

export type QuestionType = 'Meal' | 'RSVPNote' | 'Custom';
export type AnswerType = 'Option' | 'Text';
export type AnswerDestination = 'Household' | 'Person' | 'Invitation';

export interface Option {
  id?: string;
  text: string;
  description?: string;
}

export interface Question {
  id?: string;
  text: string;
  type: QuestionType;
  answerType: AnswerType;
  answerDestination: AnswerDestination;
  options?: Option[];
  description?: string;
  isRequired: boolean;
}

export interface Answer {
  questionId: string;
  optionId?: string;
  text?: string;
}

interface GdsClientClass {
  getGuestWedding: () => Response<GuestWeddingResponse>;
  updateGuestWedding: (data: GuestWeddingBody) => Response<GuestWeddingResponse>;
  getEvents: () => Response<EventResponse[]>;
  createEvent: (weddingId: string, data: EventBody) => Response<EventResponse>;
  patchEvent: (eventId: string, data: EventBody) => Response<EventResponse>;
  putEvent: (eventId: string, data: EventBody) => Response<EventResponse>;
  deleteEvent: (eventId: string) => Response<EventResponse>;
  createMeal: (eventId: string, meal: string) => Response<EventResponse>;
  updateMeal: (eventId: string, updateBody: UpdateMealBody) => Response<EventResponse>;
  deleteMeal: (eventId: string, meal: string) => Response<any>;
  getHouseholds: () => Response<HouseholdsResponse[]>;
  createHousehold: (weddingId: string, household: Household) => Response<HouseholdResponse>;
  updateHousehold: (weddingId: string, household: Household) => Response<HouseholdResponse>;
  deleteHousehold: (weddingId: string, household: Household) => Response<any>;
  upsertHouseholdBulk: (households: Household[]) => Response<any>;
}

class GdsClient implements GdsClientClass {
  protected apiKey: string;

  public constructor() {
    const key = process.env.GDS_API_KEY || process.env.REACT_APP_GDS_API_KEY;
    if (!key) {
      throw new Error('GDS API key is required');
    }

    this.apiKey = key;
  }

  private getGdsHost = (): string => {
    if (process.env.GDS_ENV === 'production' || process.env.REACT_APP_GDS_ENV === 'production') {
      return 'https://api.guests.xogrp.com';
    }

    return 'https://qa-api.guests.xogrp.com';
  };

  private callGdsApi(path: string, config: AxiosRequestConfig = {}): AxiosPromise<any> {
    return axios({
      url: path,
      ...config,
      headers: {
        ...this.baseHeaders(),
        ...config.headers,
      },
    });
  }

  private getSessionToken = (): string => {
    return Cookies.get('xo-session-token') || '';
  };

  private baseHeaders = (): Headers => {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'MEMBERSHIP-SESSION-TOKEN': this.getSessionToken(),
      'X-API-KEY': this.apiKey,
    };
  };

  public downloadResponses = (): Response<any> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/rsvp/export`, { method: 'GET' });
  };

  /*
   * Guest Wedding Methods
   */

  public getGuestWedding = (): Response<GuestWeddingResponse> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/guest-weddings`, {
      method: 'GET',
    });
  };

  public updateGuestWedding = (data: GuestWeddingBody): Response<GuestWeddingResponse> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/guest-weddings`, {
      method: 'PATCH',
      data,
    });
  };

  /*
   * Event Methods
   */

  public getEvents = (): Response<EventResponse[]> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/events`, {
      method: 'GET',
    });
  };

  public createEvent = (weddingId: string, data: Omit<EventBody, 'id'>): Response<EventResponse> => {
    const { date, ...requestBody } = data;
    if (date) {
      // @ts-ignore missing type
      // eslint-disable-next-line
      requestBody['date'] = date;
    }
    const withId = { weddingId, ...sanitizeParams(requestBody) };
    return this.callGdsApi(`${this.getGdsHost()}/v1/events`, {
      method: 'POST',
      data: withId,
    });
  };

  public patchEvent = (eventId: string, data: EventBody): Response<EventResponse> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/events/${eventId}`, {
      method: 'PATCH',
      data: sanitizeParams(data),
    });
  };

  public putEvent = (eventId: string, data: EventBody): Response<EventResponse> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/events/${eventId}`, {
      method: 'PUT',
      data: sanitizeParams(data),
    });
  };

  public deleteEvent = (eventId: string): Response<EventResponse> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/events/${eventId}`, {
      method: 'DELETE',
    });
  };

  /*
   * Meals Methods
   */
  public createMeal = (eventId: string, meal: string): Response<EventResponse> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/events/${eventId}/meals`, {
      method: 'POST',
      data: {
        meal,
      },
    });
  };

  public updateMeal = (eventId: string, mealBody: UpdateMealBody): Response<EventResponse> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/events/${eventId}/meals`, {
      method: 'PATCH',
      data: mealBody,
    });
  };

  public deleteMeal = (eventId: string, meal: string): Response<any> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/events/${eventId}/meals`, {
      method: 'DELETE',
      data: {
        meal,
      },
    });
  };

  /*
   * Household Methods
   */
  public getHouseholds = (): Response<HouseholdsResponse[]> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/households`, {
      method: 'GET',
    });
  };

  public createHousehold = (weddingId: string, household: Omit<Household, 'id'>): Response<HouseholdResponse> => {
    const data = sanitizeParams({
      weddingId,
      groupId: null,
      ...household,
    });
    return this.callGdsApi(`${this.getGdsHost()}/v1/households`, {
      method: 'POST',
      data,
    });
  };

  public updateHousehold = (weddingId: string, household: Household): Response<HouseholdResponse> => {
    const { id: householdId } = household;
    const data = sanitizeParams({ weddingId, groupId: null, ...household });
    return this.callGdsApi(`${this.getGdsHost()}/v1/households/${householdId}`, {
      method: 'PUT',
      data,
    });
  };

  public deleteHousehold = (weddingId: string, household: Household): Response<any> => {
    const { id: householdId } = household;
    const data = sanitizeParams({ weddingId, groupId: null, ...household });
    return this.callGdsApi(`${this.getGdsHost()}/v1/households/${householdId}`, {
      method: 'DELETE',
      data,
    });
  };

  public upsertHouseholdBulk = (households: Household[]): Response<any> => {
    const sanitizedData = sanitizeParams({ ...households });
    const data = Object.values(sanitizedData);
    return this.callGdsApi(`${this.getGdsHost()}/v1/households/bulk`, {
      method: 'POST',
      data,
    });
  };

  /*
    Wedding Methods
  */

  public getAttendanceList = (weddingId: string, householdID: string): Response<any> => {
    return this.callGdsApi(`${this.getGdsHost()}/v1/weddings/${weddingId}/attendance/${householdID}`, {
      method: 'GET',
    });
  };
}

export default GdsClient;
export { sanitizeParams };
